import React, { useState, useEffect, useRef } from "react";
import SingleDamagedPartModal from "../components/SingleDamagePartModal";
import { CarSVG } from "../components/CarSVG";

const CarDamageAssessment = ({ isLoading, assessmentData }) => {
  const [partName, setPartName] = useState(null);
  const [partsDamageMap, setPartsDamageMap] = useState({});
  const [clickedPart, setClickedPart] = useState(null);
  const carSvgRef = useRef(null);

  // Damaged Parts Modal state
  const [damagedPartsModalState, setDamagedPartsModalState] = useState({
    isOpen: false,
    initialFilter: "all",
    initialSeverityFilter: "",
    initialActiveView: 0,
    selectedPartDetail: null,
  });

  // Damage thresholds for different parts
  const damageThresholds = {
    bonnet: 7,
    cap: 7,
    dickie: 7,
    "front bumper": 7,
    "front windshield": 7,
    grill: 7,
    "left apillar": 7,
    "left fender": 7,
    "left fog lamp": 7,
    "left front door": 7,
    "left front rim": 9,
    "left front window": 7,
    "left head lamp": 7,
    "left mirror": 7,
    "left quarter glass": 7,
    "left quarter panel": 7,
    "left rear door": 7,
    "left rear rim": 9,
    "left rear window": 7,
    "left running board": 7,
    "left tail lamp": 7,
    logo: 7,
    "rear bumper": 7,
    "rear windshield": 7,
    "right apillar": 7,
    "right fender": 7,
    "right fog lamp": 7,
    "right front door": 7,
    "right front rim": 9,
    "right front window": 7,
    "right head lamp": 7,
    "right mirror": 7,
    "right quarter glass": 7,
    "right quarter panel": 7,
    "right rear door": 7,
    "right rear rim": 9,
    "right rear window": 7,
    "right running board": 7,
    "right tail lamp": 7,
  };

  useEffect(() => {
    if (assessmentData && assessmentData.assessment_details) {
      processAssessmentData(assessmentData);
    }
  }, [assessmentData]);

  const openDamagedPartsModal = (
    initialFilter = "all",
    initialSeverityFilter = "",
    selectedPartDetail = null
  ) => {
    const currentActiveView = carSvgRef.current?.getActiveView?.() || 0;
    setDamagedPartsModalState({
      isOpen: true,
      initialFilter,
      initialSeverityFilter,
      initialActiveView: currentActiveView,
      selectedPartDetail,
    });
  };

  const processAssessmentData = (data) => {
    const processedParts = {};
    const damagedParts = [];

    data.assessment_details.forEach((part) => {
      const svgId = part.part_name
        .toLowerCase()
        .split(" ")
        .join("-")
        .split("_")
        .join("-");

      const intensity = part.Intensity || 0;
      const healthData = calculateHealthStatus(intensity, svgId);

      const partData = {
        name: part.part_name,
        svgId: svgId,
        action: part.Action,
        intensity: intensity,
        healthScore: healthData.score,
        healthText: healthData.text,
        color: healthData.color,
        damageImages: part.damage_images || [],
        partImages: part.part_images || [],
        side: part.side,
        partIds: part.part_ids || [],
        editComments: part.edit_comments || [],
      };

      processedParts[svgId] = partData;

      if (intensity > 0 && part.Action !== "No Damage") {
        damagedParts.push(partData);
      }
    });

    damagedParts.sort((a, b) => b.intensity - a.intensity);

    setPartsDamageMap(processedParts);
  };

  const calculateHealthStatus = (intensity, partId) => {
    const threshold = damageThresholds[partId] || 3;

    let score, text, color;

    if (intensity === 0) {
      score = 5;
      text = "Good";
      color = "rgba(34, 197, 94, 0.8)"; // green-500
    } else if (intensity > 0 && intensity <= 2) {
      score = 4;
      text = "Moderate Damage";
      color = "rgba(251, 191, 36, 0.8)"; // amber-400
    } else if (intensity > 2 && intensity <= threshold) {
      score = 3;
      text = "Minor Damage";
      color = "rgba(251, 146, 60, 0.8)"; // orange-400
    } else if (intensity > threshold) {
      score = 2;
      text = "Severe Damage";
      color = "rgba(239, 58, 58, 0.8)"; // red-500
    }

    return { score, text, color };
  };

  const closeDamagedPartsModal = () => {
    setDamagedPartsModalState({
      isOpen: false,
      initialFilter: "all",
      initialSeverityFilter: "",
      initialActiveView: 0,
      selectedPartDetail: null,
    });
    setClickedPart(null);
  };
  return (
    <div className="w-full">
      {/* <h2 className="text-xl font-semibold text-gray-800 mb-4">Vehicle Damage View</h2> */}
      <CarSVG
        ref={carSvgRef}
        partsDamageMap={partsDamageMap}
        isLoading={isLoading}
        setPartName={setPartName}
        openDamagedPartsModal={openDamagedPartsModal}
        clickedPart={clickedPart}
        setClickedPart={setClickedPart}
      />
      {/* <DamageLegend /> */}
      {damagedPartsModalState.isOpen && (
        <SingleDamagedPartModal
          isOpen={damagedPartsModalState.isOpen}
          onClose={closeDamagedPartsModal}
          assessmentData={assessmentData}
          initialFilter={damagedPartsModalState.initialFilter}
          initialSeverityFilter={damagedPartsModalState.initialSeverityFilter}
          initialActiveView={damagedPartsModalState.initialActiveView}
          partDetail={
            damagedPartsModalState.selectedPartDetail || partName
          }
          partsDamageMap={partsDamageMap}
          setPartName={setPartName}
          clickedPart={clickedPart}
          setClickedPart={setClickedPart}
          // onRefreshAssessment={refreshAssessmentData}
        />
      )}
    </div>
  );
};

export default CarDamageAssessment;
